import { useEffect, useRef, useState } from "react";

/**
 * CountUp — animates from 0 to numeric value when element enters viewport.
 * If `value` is not numeric (e.g. "2.3M"), renders as-is.
 */
export default function CountUp({ value, duration = 1400, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(() => {
    const n = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? "0" : String(value);
  });

  useEffect(() => {
    const numeric = Number(String(value).replace(/[^0-9.-]/g, ""));
    if (!Number.isFinite(numeric)) {
      setDisplay(String(value));
      return;
    }
    const node = ref.current;
    if (!node) return;
    let raf;
    let started = false;
    const animate = (start) => {
      const step = (ts) => {
        if (!start) start = ts;
        const progress = Math.min((ts - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = numeric * eased;
        setDisplay(
          Number.isInteger(numeric)
            ? Math.round(current).toString()
            : current.toFixed(1)
        );
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            started = true;
            animate();
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  // Preserve prefix (+, ≈) and suffix letters (M, K) of original string
  const raw = String(value);
  const prefix = raw.match(/^[+≈~><]/)?.[0] || "";
  const suffix = raw.match(/[a-zA-Z]+$/)?.[0] || "";
  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
